/**
 * Staff panel for moving a bring-your-own domain's nameservers to Cloudflare.
 *
 * The panel exists to make one moment reviewable: the point between "we built
 * the zone" and "the customer switches their nameservers". Once they switch, we
 * are authoritative for the whole domain, and anything discovery missed simply
 * stops resolving — most consequentially their email.
 *
 * So the imported records are shown in full, the records we deliberately left
 * behind are shown separately (that list should contain their old website and
 * nothing else), and the nameservers are withheld behind an explicit
 * acknowledgement that someone compared this against the registrar.
 */

import { useState, useEffect, useCallback } from 'react';
import { Card } from './ui/Card';
import { Button } from './ui/Button';
import { Spinner } from './ui/Spinner';
import { Badge } from './ui/Badge';
import {
  getDelegation,
  prepareDelegation,
  type DelegationStatus,
  type DelegationRecord,
} from '../api/domains';

interface DomainDelegationProps {
  websiteId: string;
  /** Shown for context; the domain itself comes from the delegation record. */
  customDomain?: string | null;
}

function RecordTable({ records, empty }: { records: DelegationRecord[]; empty: string }) {
  if (records.length === 0) {
    return <p className="text-sm text-gray-500 italic">{empty}</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase text-gray-500">
            <th className="py-1 pr-4">Type</th>
            <th className="py-1 pr-4">Name</th>
            <th className="py-1 pr-4">Value</th>
          </tr>
        </thead>
        <tbody className="font-mono text-xs">
          {records.map((r, i) => (
            <tr key={`${r.type}-${r.name}-${i}`} className="border-t border-gray-100">
              <td className="py-1 pr-4 font-semibold">{r.type}</td>
              <td className="py-1 pr-4">{r.name}</td>
              <td className="py-1 pr-4 break-all">
                {r.priority !== undefined ? `${r.priority} ` : ''}
                {r.content}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export function DomainDelegation({ websiteId, customDomain }: DomainDelegationProps) {
  const [delegation, setDelegation] = useState<DelegationStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [preparing, setPreparing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reviewed, setReviewed] = useState(false);
  const [copied, setCopied] = useState(false);
  const [pollingStopped, setPollingStopped] = useState(false);

  const status = delegation?.delegationStatus ?? null;

  const copyValue = useCallback((value: string) => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }, []);

  const load = useCallback(async () => {
    try {
      setDelegation(await getDelegation(websiteId));
    } catch (err) {
      console.error('Failed to load delegation status:', err);
    } finally {
      setLoading(false);
    }
  }, [websiteId]);

  useEffect(() => {
    load();
  }, [load]);

  /**
   * Poll while the delegation is mid-flight.
   *
   * The cron completes this on its own once Cloudflare marks the zone active,
   * so the only thing standing between a finished delegation and a panel that
   * says so was a page reload. Watching a screen that cannot change is the
   * least useful way to wait.
   *
   * Stops on any terminal state, and gives up after twenty minutes rather than
   * polling a tab someone left open. Nameserver propagation routinely outlasts
   * that, so the give-up message says to refresh rather than implying failure.
   */
  const inProgress = status === 'preparing' || status === 'awaiting-nameservers';

  useEffect(() => {
    if (!inProgress) return;

    let polls = 0;
    const MAX_POLLS = 40; // 40 x 30s = 20 minutes

    const timer = setInterval(() => {
      polls += 1;
      if (polls > MAX_POLLS) {
        clearInterval(timer);
        setPollingStopped(true);
        return;
      }
      load();
    }, 30_000);

    return () => clearInterval(timer);
  }, [inProgress, load]);

  const handlePrepare = async () => {
    setPreparing(true);
    setError(null);
    try {
      const result = await prepareDelegation(websiteId);
      if (!result.success && result.reason === 'ownership-unverified') {
        setError(result.message);
      }
    } catch (err) {
      // apiRequest throws on any non-2xx, so a 412 arrives here rather than as
      // a body — its message is already the ownership explanation.
      setError(err instanceof Error ? err.message : 'Failed to prepare delegation');
    } finally {
      // Reload however this ended. Previously this only ran on the success
      // path, so a rejected prepare left the panel showing whatever state it
      // had at mount — including, when ownership was the problem, no sign of
      // the record the error text was telling the reader to look for.
      await load();
      setPreparing(false);
    }
  };

  if (loading) {
    return (
      <Card className="p-6">
        <Spinner />
      </Card>
    );
  }

  const snapshot = delegation?.importSnapshot ?? null;

  // Our website records are the ones pointing at the Pages project; everything
  // else in the zone is the customer's.
  const zoneRecords = delegation?.currentRecords ?? [];
  const ourRecords = zoneRecords.filter((r) => r.content.includes('.pages.dev'));
  const theirRecords = zoneRecords.filter((r) => !r.content.includes('.pages.dev'));

  return (
    <Card className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">Nameserver Delegation</h3>
          <p className="text-sm text-gray-500">
            Move {customDomain || delegation?.domain || 'this domain'} to Cloudflare so the
            root domain works without a redirect.
          </p>
        </div>
        {status && (
          <Badge variant={status === 'active' ? 'success' : status === 'failed' ? 'error' : 'warning'}>
            {status}
          </Badge>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {!status && (
        <div className="space-y-3">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm text-yellow-800">
            <p className="font-medium mb-1">This makes us the customer’s DNS provider.</p>
            <p className="text-xs text-yellow-700">
              Preparing creates a Cloudflare zone and copies the records we can find into
              it. The zone stays dormant until the customer switches nameservers, so this
              step is safe on its own — but discovery cannot be exhaustive, and a missed MX
              record means their email stops arriving once they do switch.
            </p>
          </div>

          {/* Ownership gate. Only this path needs one: creating a zone happens
              before any DNS change, so unlike the CNAME route there is nothing
              about the customer's DNS that already demonstrates control. */}
          {delegation?.ownership.record && !delegation.ownership.verified && (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-2">
              <p className="text-sm font-semibold text-gray-800">
                Ownership not yet verified
              </p>
              <p className="text-xs text-gray-600">
                A zone we hold for a domain the customer does not own can block its real
                owner from adding it to their own Cloudflare account. Have them add this
                record first — it is checked, not decorative.
              </p>
              <div className="bg-white rounded border border-gray-200 p-3 grid grid-cols-3 gap-2 text-xs font-mono">
                <div>
                  <span className="text-gray-500">Type</span>
                  <div className="font-semibold">{delegation.ownership.record.type}</div>
                </div>
                <div>
                  <span className="text-gray-500">Name</span>
                  <div className="break-all">{delegation.ownership.record.name}</div>
                </div>
                <div className="flex items-start justify-between gap-1">
                  <div className="min-w-0">
                    <span className="text-gray-500">Value</span>
                    <div className="break-all">{delegation.ownership.record.value}</div>
                  </div>
                  <button
                    onClick={() => copyValue(delegation.ownership.record!.value)}
                    className="text-blue-600 hover:text-blue-800 shrink-0"
                  >
                    {copied ? '✓' : 'Copy'}
                  </button>
                </div>
              </div>
              <button onClick={load} className="text-xs text-blue-600 hover:text-blue-800">
                Re-check ownership
              </button>
            </div>
          )}

          {delegation?.ownership.verified && (
            <p className="text-xs text-green-700">
              ✓ Ownership verified — the <span className="font-mono">_niftee-verify</span>{' '}
              record is present.
            </p>
          )}

          <Button
            onClick={handlePrepare}
            loading={preparing}
            disabled={!delegation?.ownership.verified}
          >
            Prepare delegation
          </Button>
          {!delegation?.ownership.verified && (
            <p className="text-xs text-gray-500">
              Available once the ownership record resolves.
            </p>
          )}
        </div>
      )}

      {status === 'awaiting-nameservers' && delegation && (
        <div className="space-y-4">
          {snapshot && (
            <>
              {/* The zone's ACTUAL contents, not our import's diff.
                  This panel previously listed only what OUR import created —
                  so records Cloudflare's own scan wrote straight into the zone
                  appeared in neither list. On the first real delegation it
                  reported "Imported (1)" for a zone holding four of the
                  customer's records, and the reviewer would have been ticking
                  the confirmation box against a quarter of the zone. The whole
                  point of this step is comparing against the registrar, so it
                  has to show what is actually there. */}
              <div>
                <p className="text-sm font-semibold text-gray-700 mb-1">
                  Now in the zone ({theirRecords.length})
                </p>
                <p className="text-xs text-gray-500 mb-1">
                  Everything carried over from their existing DNS. Compare this against the
                  registrar — this is the list that matters.
                </p>
                <RecordTable
                  records={theirRecords}
                  empty="No records carried over. Confirm against the registrar — an empty result is normal for a parked domain and alarming for an active one."
                />
              </div>

              <div>
                <p className="text-sm font-semibold text-gray-700 mb-1">
                  Website records we added ({ourRecords.length})
                </p>
                <RecordTable records={ourRecords} empty="None yet." />
              </div>

              <div>
                <p className="text-sm font-semibold text-gray-700 mb-1">
                  Replaced ({snapshot.removed.length})
                </p>
                <p className="text-xs text-gray-500 mb-1">
                  Their old website records, removed so ours can take those names. Anything
                  else here is a bug.
                </p>
                {snapshot.removed.length === 0 ? (
                  <p className="text-sm text-gray-500 italic">Nothing replaced.</p>
                ) : (
                  <ul className="text-xs font-mono text-gray-600 space-y-0.5">
                    {snapshot.removed.map((r) => (
                      <li key={r}>{r}</li>
                    ))}
                  </ul>
                )}
              </div>

              {snapshot.failed.length > 0 && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm font-semibold text-red-800 mb-1">
                    Failed to import ({snapshot.failed.length})
                  </p>
                  <ul className="text-xs text-red-700 space-y-1 font-mono">
                    {snapshot.failed.map((f, i) => (
                      <li key={i}>
                        {f.record.type} {f.record.name} — {f.error}
                      </li>
                    ))}
                  </ul>
                  <p className="text-xs text-red-700 mt-2">
                    Add these by hand before the customer switches.
                  </p>
                </div>
              )}
            </>
          )}

          <label className="flex items-start gap-2 text-sm text-gray-700">
            <input
              type="checkbox"
              checked={reviewed}
              onChange={(e) => setReviewed(e.target.checked)}
              className="mt-1"
            />
            <span>
              I have compared this list against the customer’s registrar DNS panel,
              including MX records.
            </span>
          </label>

          {reviewed ? (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm font-semibold text-blue-900 mb-2">
                Nameservers for the customer to set
              </p>
              <ul className="font-mono text-sm text-blue-800 space-y-1">
                {delegation.nameservers.map((ns) => (
                  <li key={ns}>{ns}</li>
                ))}
              </ul>
              <p className="text-xs text-blue-700 mt-2">
                Cloudflare marks the zone active once it sees these answering — usually
                within a few hours. The cron finishes the connection automatically.
                Zone is currently: <span className="font-mono">{delegation.zoneStatus ?? 'unknown'}</span>
              </p>
              <p className="text-xs text-blue-600 mt-1">
                {pollingStopped
                  ? 'Stopped checking after 20 minutes — refresh to see the current state. Nameserver changes often take longer than that.'
                  : 'This panel refreshes itself every 30 seconds.'}
              </p>
            </div>
          ) : (
            <p className="text-xs text-gray-500">
              Nameservers appear once the record review is confirmed.
            </p>
          )}
        </div>
      )}

      {status === 'active' && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-sm text-green-800">
          Nameservers are pointed at Cloudflare and the zone is active. The root domain
          serves the site directly, with no redirect.
        </div>
      )}

      {status === 'failed' && (
        <div className="space-y-3">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-red-700">
            Preparation failed. Nothing was handed to the customer and their DNS is
            unchanged. Check the worker logs, then retry.
          </div>
          <Button onClick={handlePrepare} loading={preparing} variant="secondary">
            Retry preparation
          </Button>
        </div>
      )}
    </Card>
  );
}
